import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { SessionManager } from './utils/sessionManager';
import ErrorBoundary from './components/ErrorBoundary';
// Auth pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';

// Client pages
import Dashboard from './pages/client/Dashboard';
import Profile from './pages/client/Profile';
import Pricing from './pages/client/Pricing';
import APIPricing from './pages/client/APIPricing';
import SubscriptionHistory from './pages/client/SubscriptionHistory';
import History from './pages/client/History';
import SubUsers from './pages/client/SubUsers';
import BuyCredits from './pages/client/BuyCredits';

// Admin pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AllUser from './pages/admin/AllUser';
import AddUser from './pages/admin/AddUser';
import Reports from './pages/admin/Reports';
import AddCredits from './pages/admin/AddCredits';
import PendingUsers from './pages/admin/PendingUsers';

// Verification pages
import KYCVerification from './pages/verification/KYCVerification';
import VerifyPAN from './pages/verification/VerifyPAN';
import VerifyGSTIN from './pages/verification/VerifyGSTIN';
import VerifyBankAccount from './pages/verification/VerifyBankAccount';
import ApiDocs from './pages/ApiDocs';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  // Only check if session exists, don't validate expiry on every render
  const session = SessionManager.getSession();
  const user = session?.user;

  if (!session) {
    return <Navigate to="/" replace />;
  }

  if (adminOnly && user?.role !== 'Admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route Component (redirects if already logged in)
const PublicRoute = ({ children }) => {
  // Only check if session exists, don't validate expiry on every render
  const session = SessionManager.getSession();
  const user = session?.user;

  if (session) {
    // Redirect based on role
    if (user?.role === 'Admin') {
      return <Navigate to="/admin" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

function App() {
  useEffect(() => {
    // Global error handler to prevent app crashes
    const handleError = (event) => {
      // console.error('Global error caught:', event.error);
      // Prevent the error from crashing the app
      event.preventDefault();
    };

    const handleUnhandledRejection = (event) => {
      // console.error('Unhandled promise rejection:', event.reason);
      // Prevent the error from crashing the app
      event.preventDefault();
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Check for inactivity every 2 minutes (less aggressive)
    const inactivityChecker = setInterval(() => {
      const session = SessionManager.getSession();
      if (session) {
        const inactivityTime = Date.now() - session.lastActivity;
        // Add 30 second buffer to prevent premature logout
        if (inactivityTime >= (15 * 60 * 1000 + 30000)) {
          SessionManager.clearSession();
          sessionStorage.setItem('logout_reason', 'Your session expired due to 15 minutes of inactivity. Please login again.');
          window.location.href = '/';
        }
      }
    }, 120000); // Check every 2 minutes (less aggressive)

    return () => {
      clearInterval(inactivityChecker);
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return (
    <ErrorBoundary>
      <Router>
        <div className="App">
          <Routes>
            {/* Public Routes */}
            <Route 
              path="/" 
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              } 
            />
            <Route 
              path="/register" 
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              } 
            />
            <Route path="/api-docs" element={<ApiDocs />} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kyc-verification"
            element={
              <ProtectedRoute>
                <KYCVerification />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            }
          />
          {/* Hidden Routes - Temporarily disabled */}
          {/* 
          <Route
            path="/subscription-history"
            element={
              <ProtectedRoute>
                <SubscriptionHistory />
              </ProtectedRoute>
            }
          />
          */}
          <Route
            path="/all-clients"
            element={
              <ProtectedRoute>
                <AllUser />
              </ProtectedRoute>
            }
          />
          <Route
            path="/all-user"
            element={
              <ProtectedRoute>
                <SubUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/add-user"
            element={
              <ProtectedRoute>
                <AddUser />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/buy-credits"
            element={
              <ProtectedRoute>
                <BuyCredits />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pricing"
            element={
              <ProtectedRoute>
                <Pricing />
              </ProtectedRoute>
            }
          />
          <Route
            path="/verify/pan"
            element={
              <ProtectedRoute>
                <VerifyPAN />
              </ProtectedRoute>
            }
          />
          <Route
            path="/verify/gstin"
            element={
              <ProtectedRoute>
                <VerifyGSTIN />
              </ProtectedRoute>
            }
          />
          <Route
            path="/verify/bank-account"
            element={
              <ProtectedRoute>
                <VerifyBankAccount />
              </ProtectedRoute>
            }
          />
          <Route
            path="/history"
            element={
              <ProtectedRoute>
                <History />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute adminOnly={true}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/pending-users"
            element={
              <ProtectedRoute adminOnly={true}>
                <PendingUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/api-pricing"
            element={
              <ProtectedRoute adminOnly={true}>
                <APIPricing />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/add-credits"
            element={
              <ProtectedRoute adminOnly={true}>
                <AddCredits />
              </ProtectedRoute>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Router>
    </ErrorBoundary>
  );
}

export default App;
